import React from "react";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";

import {
  Grid,
  Typography as MuiTypography,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Box,
} from "@material-ui/core";

import { Activity, CreditCard } from "react-feather";
import PaymentTable from "../../components/PaymentTable";
import { useTranslation } from "react-i18next";

const PageSurface = styled.div`
  position: relative;
  padding: 8px 0 12px;

  &:before {
    content: "";
    position: absolute;
    inset: 0 0 auto;
    height: 280px;
    border-radius: 30px;
    background: radial-gradient(
        circle at top left,
        rgba(14, 165, 233, 0.16),
        transparent 38%
      ),
      radial-gradient(
        circle at top right,
        rgba(34, 197, 94, 0.12),
        transparent 32%
      ),
      linear-gradient(135deg, #f8fbff 0%, #eef6ff 50%, #f8fafc 100%);
    z-index: 0;
  }
`;

const HeroCard = styled(MuiCard)`
  position: relative;
  z-index: 1;
  overflow: hidden;
  border-radius: 28px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 28px 60px rgba(15, 23, 42, 0.12);
  background: linear-gradient(135deg, #0f172a 0%, #142b47 52%, #15416d 100%);
`;

const HeroContent = styled(MuiCardContent)`
  position: relative;
  padding: 30px 32px;

  &:last-child {
    padding-bottom: 30px;
  }

  &:before,
  &:after {
    content: "";
    position: absolute;
    border-radius: 999px;
    pointer-events: none;
  }

  &:before {
    width: 220px;
    height: 220px;
    right: -40px;
    top: -90px;
    background: rgba(56, 189, 248, 0.18);
  }

  &:after {
    width: 160px;
    height: 160px;
    right: 90px;
    bottom: -100px;
    background: rgba(34, 197, 94, 0.14);
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  backdrop-filter: blur(10px);
`;

const HeroTitle = styled(MuiTypography)`
  color: #f8fafc;
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.08;
`;

const HeroText = styled(MuiTypography)`
  max-width: 720px;
  color: rgba(226, 232, 240, 0.9);
  line-height: 1.7;
`;

const MiniStatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 22px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const MiniStat = styled.div`
  padding: 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);

  strong {
    display: block;
    margin-top: 8px;
    color: #ffffff;
    font-size: 1rem;
    font-weight: 800;
  }

  span {
    color: rgba(226, 232, 240, 0.82);
    font-size: 0.88rem;
  }
`;

const SectionCard = styled(MuiCard)`
  position: relative;
  z-index: 1;
  margin-top: 28px;
  border-radius: 26px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.08);
  overflow: hidden;
`;

const SectionContent = styled(MuiCardContent)`
  padding: 0;

  &:last-child {
    padding-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 28px 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SectionTitle = styled(MuiTypography)`
  color: #0f172a;
  font-weight: 800;
  letter-spacing: -0.03em;
`;

const SectionText = styled(MuiTypography)`
  color: #64748b;
  max-width: 620px;
`;

const SectionPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  background: #eff6ff;
  color: #0369a1;
  font-weight: 700;
  white-space: nowrap;
`;

function Payments() {
  const { t } = useTranslation();
  return (
    <React.Fragment>
      <Helmet title={t("payments.title")} />
      <PageSurface>
        <HeroCard>
          <HeroContent>
            <Eyebrow>
              <Activity size={14} />
              {t("payments.center")}
            </Eyebrow>
            <HeroTitle variant="h2" mt={4} mb={2}>
              {t("payments.history")}
            </HeroTitle>
            <HeroText variant="body1">{t("payments.description")}</HeroText>
            <MiniStatRow>
              <MiniStat>
                <span>{t("payments.processed")}</span>
                <strong>{t("payments.centralTracking")}</strong>
              </MiniStat>
              <MiniStat>
                <span>{t("payments.creditStatus")}</span>
                <strong>{t("payments.immediateReview")}</strong>
              </MiniStat>
              <MiniStat>
                <span>{t("payments.transactionDetail")}</span>
                <strong>{t("payments.receiptAccess")}</strong>
              </MiniStat>
            </MiniStatRow>
          </HeroContent>
        </HeroCard>

        <SectionCard>
          <SectionContent>
            <SectionHeader>
              <Box>
                <SectionTitle variant="h4">
                  {t("payments.registeredMovements")}
                </SectionTitle>
                <SectionText variant="body2">
                  {t("payments.registeredDescription")}
                </SectionText>
              </Box>
              <SectionPill>
                <CreditCard size={16} />
                {t("payments.financialHistory")}
              </SectionPill>
            </SectionHeader>
            <Grid container>
              <Grid item xs={12}>
                <PaymentTable />
              </Grid>
            </Grid>
          </SectionContent>
        </SectionCard>
      </PageSurface>
    </React.Fragment>
  );
}

export default Payments;
